import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { client } from "../api/client";
import Dropdown from "../components/Dropdown";
import "./VolunteerApplication.css";

// Mirrors backend volunteers/models.py (DEPARTMENT_CHOICES, SUGGESTED_GRADES,
// User.GENDER_CHOICES).
const DEPARTMENTS = [
  { value: "admin", label: "Admin" },
  { value: "media", label: "Media" },
  { value: "illustration", label: "Illustration" },
  { value: "story", label: "Story" },
];
const GRADES = ["8th Grade", "9th Grade", "10th Grade", "11th Grade", "12th Grade", "Other"];
const GENDERS = [
  { value: "male", label: "male" },
  { value: "female", label: "female" },
  { value: "other", label: "other" },
  { value: "prefer_not_to_say", label: "prefer not to say" },
];

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  gender: "",
  date_of_birth: "",
  email: "",
  phone_number: "",
  school: "",
  grade: "",
  preferred_department_1: "",
  preferred_department_2: "",
  photo_consent: null,
  signature_name: "",
  signature_date: "",
};

const PAGE_ZOOM = 0.7;

const inputClass =
  "w-full h-[56px] lg:h-[67px] bg-[#d2d4d7] rounded-[10px] px-[22px] text-[18px] lg:text-[20px] text-[#1c2938] placeholder:text-[#515151] outline-none focus:ring-2 focus:ring-[#1c2938]/30";
// Native date inputs get their zoom compensated back to 1 so the browser's
// calendar popup renders full-size; dimensions are pre-scaled by PAGE_ZOOM so
// the box still lines up with the other (page-zoomed) inputs.
const dateWrapStyle = { zoom: 1 / PAGE_ZOOM };
const dateInputClass =
  "w-full h-[39px] lg:h-[47px] bg-[#d2d4d7] rounded-[7px] px-[15px] text-[13px] lg:text-[14px] text-[#1c2938] outline-none focus:ring-2 focus:ring-[#1c2938]/30";
const labelClass = "block text-[#5e5e5e] text-[20px] lg:text-[24px] mb-3";

function SectionHeading({ children }) {
  return (
    <div className="flex items-center gap-6 mb-8">
      <h2
        className="text-[28px] lg:text-[36px] text-[#5e5e5e] whitespace-nowrap"
        style={{ fontFamily: "Koulen, sans-serif" }}
      >
        {children}
      </h2>
      <div className="flex-1 h-px bg-[#515151]" />
    </div>
  );
}

function ConsentOption({ checked, onSelect, children }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full max-w-[965px] h-[56px] lg:h-[67px] bg-[#d2d4d7] rounded-[10px] flex items-center gap-3 px-[24px] text-left"
    >
      <span
        className={`inline-block w-[19px] h-[19px] shrink-0 border border-[#d0d0d0] text-white text-[14px] leading-[17px] text-center ${
          checked ? "bg-[#1c2938]" : "bg-white"
        }`}
      >
        {checked ? "✓" : ""}
      </span>
      <span className="text-[16px] lg:text-[20px] text-[#515151]">{children}</span>
    </button>
  );
}

export default function VolunteerApplication() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [existing, setExisting] = useState(null); // my current application, if any
  const [errors, setErrors] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const loggedIn = !!localStorage.getItem("access_token");

  useEffect(() => {
    if (!loggedIn) return;
    client
      .get("/api/volunteers/applications/me/")
      .then(({ data }) => {
        setExisting(data);
        setForm({
          ...EMPTY_FORM,
          ...Object.fromEntries(
            Object.keys(EMPTY_FORM).map((k) => [k, data[k] ?? EMPTY_FORM[k]])
          ),
        });
      })
      .catch(() => {
        // No application yet — prefill what we already know from the profile.
        client
          .get("/api/users/me/")
          .then(({ data }) =>
            setForm((f) => ({
              ...f,
              first_name: data.first_name || "",
              last_name: data.last_name || "",
              email: data.email || "",
              gender: data.gender || "",
              phone_number: data.phone_number || "",
              date_of_birth: data.birth_date || "",
            }))
          )
          .catch(() => {});
      });
  }, [loggedIn]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors(null);
    setSuccess(null);

    if (form.photo_consent === null) {
      setErrors({ photo_consent: ["Please answer the photography release agreement."] });
      return;
    }

    const payload = { ...form };
    if (!payload.gender) delete payload.gender; // optional on the backend

    setSubmitting(true);
    try {
      const { data } = existing
        ? await client.put("/api/volunteers/applications/me/", payload)
        : await client.post("/api/volunteers/applications/", payload);
      setExisting(data);
      setSuccess(
        existing
          ? "Your application has been updated and is pending review."
          : "Your application has been submitted. Thank you!"
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const data = err.response?.data;
      setErrors(
        typeof data === "object" && data !== null
          ? data
          : { detail: ["Something went wrong. Please try again."] }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (name) =>
    errors?.[name] && (
      <p className="text-red-600 text-sm mt-2">
        {Array.isArray(errors[name]) ? errors[name].join(" ") : String(errors[name])}
      </p>
    );

  return (
    <div
      className="min-h-screen bg-white text-left"
      style={{
        fontFamily: "Arial, sans-serif",
        zoom: PAGE_ZOOM,
        // Compensate for zoom so the fixed (unzoomed) header still clears the title.
        paddingTop: `calc(var(--header-height, 80px) / ${PAGE_ZOOM})`,
      }}
    >
      {/* Page title */}
      <div className="px-6 sm:px-10 lg:px-[146px] pt-8 pb-6">
        <h1
          className="text-[40px] sm:text-[52px] lg:text-[64px] text-black tracking-[3.2px]"
          style={{ fontFamily: "Koulen, sans-serif" }}
        >
          Volunteer Application
        </h1>
      </div>

      {/* Double divider */}
      <div className="mx-6 sm:mx-10">
        <div className="border-t-2 border-black" />
        <div className="border-t-2 border-black mt-[11px]" />
      </div>

      <main className="px-6 sm:px-10 lg:px-[146px] pb-14">
        {!loggedIn && (
          <div className="mb-10 max-w-[965px] text-[#1c2938]">
            Please{" "}
            <Link to="/login" className="underline font-bold">
              log in
            </Link>{" "}
            to submit a volunteer application.
          </div>
        )}
        {existing && (
          <div className="mb-10 max-w-[965px] rounded-[10px] bg-[#d2d4d7] px-6 py-4 text-[#1c2938]">
            You already have an application — current status: <b>{existing.status}</b>.
            {existing.status === "approved"
              ? " Approved applications can no longer be edited."
              : " You can review and update it below."}
          </div>
        )}
        {success && (
          <div className="mb-10 max-w-[965px] rounded-[10px] bg-[#1c2938] px-6 py-4 text-white">
            {success}
          </div>
        )}
        {errors?.detail && (
          <div className="mb-10 max-w-[965px] rounded-[10px] bg-red-50 px-6 py-4 text-red-700">
            {Array.isArray(errors.detail) ? errors.detail.join(" ") : String(errors.detail)}
          </div>
        )}
        {errors?.non_field_errors && (
          <div className="mb-10 max-w-[965px] rounded-[10px] bg-red-50 px-6 py-4 text-red-700">
            {errors.non_field_errors.join(" ")}
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-[965px]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-[98px] gap-y-10">
            {/* Name — design shows a single "Full Name" field; split to match the
                backend's first_name/last_name. */}
            <div>
              <label className={labelClass}>First Name</label>
              <input
                className={inputClass}
                name="first_name"
                value={form.first_name}
                onChange={onChange}
                placeholder="first name"
                required
              />
              {fieldError("first_name")}
            </div>
            <div>
              <label className={labelClass}>Last Name</label>
              <input
                className={inputClass}
                name="last_name"
                value={form.last_name}
                onChange={onChange}
                placeholder="last name"
                required
              />
              {fieldError("last_name")}
            </div>

            <div>
              <label className={labelClass}>Gender</label>
              <Dropdown
                value={form.gender}
                onChange={(v) => setForm({ ...form, gender: v })}
                options={GENDERS}
                placeholder="choose gender"
              />
              {fieldError("gender")}
            </div>
            <div className="hidden sm:block" />

            <div>
              <label className={labelClass}>Date of Birth</label>
              <div style={dateWrapStyle}>
                <input
                  className={dateInputClass}
                  type="date"
                  name="date_of_birth"
                  value={form.date_of_birth}
                  onChange={onChange}
                  required
                />
              </div>
              {fieldError("date_of_birth")}
            </div>
            <div className="hidden sm:block" />

            <div>
              <label className={labelClass}>Email</label>
              <input
                className={inputClass}
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="email"
                required
              />
              {fieldError("email")}
            </div>
            <div>
              <label className={labelClass}>Phone Number</label>
              <input
                className={inputClass}
                type="tel"
                name="phone_number"
                value={form.phone_number}
                onChange={onChange}
                placeholder="phone number"
                required
              />
              {fieldError("phone_number")}
            </div>

            <div>
              <label className={labelClass}>School</label>
              <input
                className={inputClass}
                name="school"
                value={form.school}
                onChange={onChange}
                placeholder="school name"
                required
              />
              {fieldError("school")}
            </div>
            <div>
              <label className={labelClass}>Grade</label>
              <Dropdown
                value={form.grade}
                onChange={(v) => setForm({ ...form, grade: v })}
                options={GRADES.map((g) => ({ value: g, label: g }))}
                placeholder="choose grade"
              />
              {fieldError("grade")}
            </div>

            {/* Not in the Figma design yet — required by the backend. */}
            <div>
              <label className={labelClass}>Preferred Department (1st)</label>
              <Dropdown
                value={form.preferred_department_1}
                onChange={(v) => setForm({ ...form, preferred_department_1: v })}
                options={DEPARTMENTS}
                placeholder="choose department"
              />
              {fieldError("preferred_department_1")}
            </div>
            <div>
              <label className={labelClass}>Preferred Department (2nd)</label>
              <Dropdown
                value={form.preferred_department_2}
                onChange={(v) => setForm({ ...form, preferred_department_2: v })}
                options={DEPARTMENTS.filter((d) => d.value !== form.preferred_department_1)}
                placeholder="choose department"
              />
              {fieldError("preferred_department_2")}
            </div>
          </div>

          {/* Photography Release Agreement */}
          <div className="mt-20">
            <SectionHeading>Photography Release Agreement</SectionHeading>
            <p className="text-[16px] lg:text-[20px] text-[#515151] max-w-[952px] mb-8">
              I understand that while attending any event or class image may appear in
              CHOPSTICKSTORY-related photographs, videos, or on the website without recompense
              or royalty.
            </p>
            <div className="flex flex-col gap-6">
              <ConsentOption
                checked={form.photo_consent === true}
                onSelect={() => setForm({ ...form, photo_consent: true })}
              >
                Yes, I give consent for CHOPSTICKSTORY to photograph for any events
              </ConsentOption>
              <ConsentOption
                checked={form.photo_consent === false}
                onSelect={() => setForm({ ...form, photo_consent: false })}
              >
                No, I do not consent to being photographed during CHOPSTICKSTORY events
              </ConsentOption>
            </div>
            {fieldError("photo_consent")}
          </div>

          {/* Participation Agreement */}
          <div className="mt-20">
            <SectionHeading>Participation Agreement</SectionHeading>
            <div className="text-[16px] lg:text-[20px] text-[#515151] max-w-[952px] space-y-6">
              <p>
                I acknowledge that I have voluntarily chosen to participate in activities at
                CHOPSTICKSTORY (the “Organization”) and understand that the Organization values
                the safety, well-being, and rights of all participants.
              </p>
              <p>
                I understand that the Organization strives to provide a safe and supportive
                environment for all participants. While every effort is made to ensure safety, I
                recognize that unforeseen accidents may occur in the course of activities. I
                voluntarily choose to participate and agree to take necessary precautions to
                ensure my own safety and the safety of others.
              </p>
              <p>
                As a participant, I agree to respect the guidelines set by the Organization and
                to engage in activities responsibly. I acknowledge that in the unlikely event of
                an accident or injury, the Organization and its representatives will provide
                appropriate assistance within reasonable capacity. I understand that my
                participation is voluntary, and I will not hold the Organization, its directors,
                officers, employees, volunteers, representatives, or affiliates (collectively,
                the “Organization Team”) responsible for any unforeseen incidents to the extent
                permitted by law.
              </p>
              <p>
                Furthermore, I agree to cooperate with the Organization in maintaining a
                positive and respectful atmosphere for all participants. If I have any concerns
                regarding safety or my participation, I understand that I am encouraged to
                communicate with the Organization Team at any time.
              </p>
              <p>
                By signing below, I confirm that I have read and understood this agreement, and
                I willingly participate under these terms.
              </p>
            </div>

            <div className="mt-12 flex flex-col gap-6 max-w-[427px]">
              <div>
                <div style={dateWrapStyle}>
                  <input
                    className={dateInputClass}
                    type="date"
                    name="signature_date"
                    value={form.signature_date}
                    onChange={onChange}
                    required
                  />
                </div>
                {fieldError("signature_date")}
              </div>
              <div>
                <input
                  className={inputClass}
                  name="signature_name"
                  value={form.signature_name}
                  onChange={onChange}
                  placeholder="full name"
                  required
                />
                {fieldError("signature_name")}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !loggedIn || existing?.status === "approved"}
            className="mt-16 h-[46px] min-w-[137px] px-6 bg-[#1c2938] text-white text-[20px] lg:text-[24px] rounded-[10px] hover:bg-[#2d3f54] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </form>
      </main>

      {/* Bottom double divider */}
      <div className="mx-6 sm:mx-10">
        <div className="border-t-2 border-black" />
        <div className="border-t-2 border-black mt-[11px]" />
      </div>
      <div className="h-10" />
    </div>
  );
}
